import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Briefcase, Plus } from "lucide-react";
import { toast } from "sonner";
import FileAttach, { AttachmentList } from "../../components/FileAttach";

const STATUSES = ["submitted", "shortlisted", "interview", "hired", "rejected"];
const EMPTY = { full_name: "", email: "", phone: "", position: "", department: "", source: "", status: "submitted", interview_at: "", interview_notes: "", cv_file_id: "" };

export default function RecruitmentPage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = () => {
    const url = filterStatus === "all" ? "/job-applications" : `/job-applications?status=${filterStatus}`;
    api.get(url).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [filterStatus]);

  const save = async () => {
    try { await api.post("/job-applications", { ...form, school_id: "demo-school-1" }); toast.success(t("success")); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const updateStatus = async (id, status) => {
    try { await api.put(`/job-applications/${id}`, { status }); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-semibold flex items-center gap-3"><Briefcase className="w-7 h-7 text-primary" /> {t("recruitment")}</h1>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44" data-testid="job-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all") || "All"}</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{t(s) || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-job-btn"><Plus className="w-4 h-4 me-2" />{t("new") || "New"}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("job_applications")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>{t("nameEn")}</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="job-name" /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>{t("position")}</Label><Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} /></div>
                <div><Label>{t("department")}</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></div>
                <div><Label>{t("application_source") || "Source"}</Label><Input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="LinkedIn" /></div>
                <div><Label>{t("interview_at") || "Interview"}</Label><Input type="datetime-local" value={form.interview_at} onChange={e => setForm({ ...form, interview_at: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>CV</Label>
                  <FileAttach
                    value={form.cv_file_id ? [{ id: form.cv_file_id, name: "CV" }] : []}
                    onChange={(list) => setForm({ ...form, cv_file_id: list[list.length - 1]?.id || null })}
                    testIdPrefix="job-cv"
                  />
                </div>
              </div>
              <Button onClick={save} className="w-full mt-3" data-testid="job-save">{t("save")}</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border shadow-none">
        <ul className="divide-y divide-border">
          {items.map(j => (
            <li key={j.id} className="p-4 flex items-center gap-3 hover:bg-muted/30" data-testid={`job-${j.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{j.full_name}</div>
                <div className="text-xs text-muted-foreground">{j.position} · {j.email} · {j.phone || "—"}{j.source ? ` · ${j.source}` : ""}</div>
                {j.interview_at && <div className="text-xs text-primary">{t("interview")}: {new Date(j.interview_at).toLocaleString()}</div>}
                {j.cv_file_id && <AttachmentList files={[{ id: j.cv_file_id, name: "CV" }]} />}
              </div>
              <Select value={j.status} onValueChange={(s) => updateStatus(j.id, s)}>
                <SelectTrigger className="w-36" data-testid={`job-status-${j.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{t(s) || s}</SelectItem>)}</SelectContent>
              </Select>
            </li>
          ))}
          {items.length === 0 && <li className="p-12 text-center text-muted-foreground">{t("no_data")}</li>}
        </ul>
      </Card>
    </div>
  );
}
